'use client';

import React from 'react';
import { Drawer, DrawerContent, LucideIcon } from '@skin-seoul/design-system';
import type { Currency } from '@skin-seoul/service';
import ShoppingBagDrawerContent from './ShoppingBagDrawerContent';
import ShoppingBagSkeleton from './ShoppingBagSkeleton';
import QueryBoundary from '../../utils/react/QueryBoundary';
import { useCartDrawer } from '../../context/CartDrawerContext';

type ShoppingBagDrawerProps = {
  currency: Currency;
  onClickContinueShopping: () => void;
  /**
   * Optional callback triggered when checkout button is clicked
   * Parent can use this for analytics tracking
   */
  onCheckoutClick?: () => void;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  /**
   * Custom Link component to use for navigation.
   * Useful for injecting Next.js Link in webapp or other routing solutions.
   * @default Link from @skin-seoul/design-system
   */
  linkComponent?: React.ElementType;
};

const ShoppingBagDrawer = ({
  currency,
  onClickContinueShopping,
  onCheckoutClick,
  ImageComponent,
  linkComponent,
}: ShoppingBagDrawerProps) => {
  const { isOpen, onSetCartState, onCartClose } = useCartDrawer();

  return (
    <Drawer direction="right" onOpenChange={(open) => onSetCartState(open)} open={isOpen}>
      <DrawerContent
        className="data-[vaul-drawer-direction=right]:w-full mobile:data-[vaul-drawer-direction=right]:max-w-[450px] items-center justify-center bg-white max-h-[100dvh]"
        tabIndex={isOpen ? undefined : -1}
        aria-hidden={!isOpen}
        id="shopping-bag-drawer-content"
      >
        <button
          aria-label="Close cart"
          className="inline-flex items-center justify-center absolute top-1.5 right-1.5 z-banner cursor-pointer bg-transparent border-none hover:bg-transparent"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onCartClose();
          }}
          tabIndex={isOpen ? 0 : -1}
        >
          <LucideIcon name="X" className="text-generic-100 w-6 h-6" />
        </button>
        <QueryBoundary loadingFallback={<ShoppingBagSkeleton />}>
          <ShoppingBagDrawerContent
            currency={currency}
            onClickContinueShopping={onClickContinueShopping}
            onCheckoutClick={onCheckoutClick}
            ImageComponent={ImageComponent}
            linkComponent={linkComponent}
          />
        </QueryBoundary>
      </DrawerContent>
    </Drawer>
  );
};

export default ShoppingBagDrawer;
